<script setup>
import { Icon } from '@components'
import { ref } from 'vue'
import { toRGB } from '../utilities'


const props = defineProps({
    classButton: {
        type: String,
    },
    buttonStyle: {
        type: String,
    },
    size: {
        type: String,
        validator(value) {
            return ['sm', 'md', 'lg', 'xl', '2xl'].includes(value)
        },
        default: 'md'
    },
    type: {
        type: String,
        validator(value) {
            return ['primary', 'secondary_gray', 'secondary_color', 'tertiary_gray', 'tertiary_color', 'link_gray'].includes(value)
        },
        default: 'primary'
    },
    radius: {
        type: String,
        validator(value) {
            return ['rounded', 'square'].includes(value)
        },
        default: 'rounded'
    },
    disable: {
        type: Boolean,
    },
    submit: {
        type: Boolean,
    },
})

/**
 * Returns the button size class based on the value of props.size.
 *
 * @return {string} The CSS classes for the button size.
 */
const buttonSizeClass = () => {
    if (props.size == 'sm') {
        return 'gap-2 px-[14px] py-[8px]'
    }
    if (props.size == 'md') {
        return 'gap-2 px-[16px] py-[10px]'
    }
    if (props.size == 'lg') {
        return 'gap-2 px-[18px] py-[10px]'
    }
    if (props.size == 'xl') {
        return 'gap-2 px-[20px] py-[12px]'
    }
    if (props.size == '2xl') {
        return 'gap-3 px-[28px] py-[16px]'
    }
}

/**
 * Returns the button type class based on the value of the 'type' prop.
 *
 * @return {string} The button type.
 */
const buttonTypeClass = () => {
    if (props.type == 'primary') {
        return 'primary'
    }
    if (props.type == 'secondary_gray') {
        return 'secondary_gray'
    }
    if (props.type == 'secondary_color') {
        return 'secondary_color'
    }
    if (props.type == 'tertiary_gray') {
        return 'tertiary_gray'
    }
    if (props.type == 'tertiary_color') {
        return 'tertiary_color'
    }
    if (props.type == 'link_gray') {
        return 'link_gray'
    }
}

const button = ref(null)

const onFocus = () => {
    const backgroundColor = button.value?.style?.backgroundColor
    const borderColor = button.value?.style?.borderColor

    if (!backgroundColor && !borderColor) return

    button.value.style.boxShadow = `0 0 0 4px ${toRGB(backgroundColor || borderColor, 15) || '#f4ebff'}, 0 1px 2px #1018280d`
}


const removeFocus = () => {
    const backgroundColor = button.value?.style?.backgroundColor
    const borderColor = button.value?.style?.borderColor

    if (!backgroundColor && !borderColor) {
        // console.log('No backgroundColor or borderColor found')
        return
    }

    button.value.style.boxShadow = 'none'
};

</script>



<template>
    <button ref="button" @focus="onFocus" @blur="removeFocus()" v-bind="$attrs" :type="props.submit ? 'submit' : 'button'" class="button-wrapper click-animation" :disabled="props.disable" :class="buttonSizeClass(), buttonTypeClass(), props.radius, props.classButton" :style="props.buttonStyle" @click="handleClick">
        <slot name="before"></slot>
        <slot></slot>
        <slot name="after"></slot>
    </button>
</template>

<style lang="scss" scoped>
.button-wrapper {
    position: relative;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    overflow: hidden;
    transition: 0.15s ease-in-out;
}

.rounded {
    border-radius: 100px !important;
}

.square {
    border-radius: 8px;
}

.primary {
    border: 1px solid #7F56D9;
    background-color: #7F56D9;
    color: #fff;
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);

    &:hover {
        border: 1px solid #6941C6;
        background-color: #6941C6;
    }

    &:focus {
        border: 1px solid #7F56D9;
        background-color: #7F56D9;
        box-shadow: 0px 0px 0px 4px #F4EBFF, 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    }

    &:disabled {
        border: 1px solid #E9D7FE;
        background-color: #E9D7FE;
    }
}

.secondary_gray {
    border: 1px solid #D0D5DD;
    background-color: #fff;
    color: #344054;
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);

    &:hover {
        border: 1px solid #D0D5DD;
        background-color: #F9FAFB;
        color: 1D2939;
    }

    &:focus {
        border: 1px solid #D0D5DD;
        background-color: #FFF;
        box-shadow: 0px 0px 0px 4px #F2F4F7, 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    }

    &:disabled {
        border: 1px solid #EAECF0;
        background-color: #fff;
        color: #D0D5DD;
    }
}

.secondary_color {
    border: 1px solid #F9F5FF;
    background-color: #F9F5FF;
    color: #6941C6;

    &:hover {
        border: 1px solid #F4EBFF;
        background-color: #F4EBFF;
        color: #53389E;
    }

    &:focus {
        border: 1px solid #F9F5FF;
        background-color: #F9F5FF;
        box-shadow: 0px 0px 0px 4px #F4EBFF, 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    }

    &:disabled {
        border: 1px solid #FCFAFF;
        background-color: #FCFAFF;
        color: #D6BBFB;
    }
}

.tertiary_color {
    border: 0px;
    background-color: #FFF;
    color: #6941C6;

    &:hover {
        background-color: #F9F5FF;
        color: #53389E;
    }

    &:focus {
        color: #6941C6;
    }

    &:disabled {
        color: #D0D5DD;
    }
}

.link_gray {
    border: 0px;
    background-color: #FFF;
    color: #475467;

    &:hover {
        background-color: #FFF;
        color: #344054;
    }

    &:focus {
        color: #475467;
    }

    &:disabled {
        color: #D0D5DD;
    }
}
</style>
